import { useQuery } from '@tanstack/react-query';
import { useUIStore } from '@/store/useUIStore';
import { getIspReadiness } from '@/api/isp';

/**
 * The prerequisites, and the modal that explains a missing one.
 *
 * Ray set them, 2026-08-31:
 *   Add a customer  → needs a MikroTik AND an internet plan
 *   Add a plan      → needs a MikroTik
 *   Add a MikroTik  → needs NOTHING. "adding server i think doesnt need
 *                     required empty profile?" — right, and the server now
 *                     creates the tenant on demand rather than refusing.
 *
 * Kept in one place so every gated button says the SAME thing about the same
 * missing piece. Wording that drifts between screens is how an operator ends up
 * believing there are two different problems.
 *
 * ⚠️ A gate NEVER blocks while readiness is still loading. Nothing is worse
 * than a button that refuses on the first click and works on the second, so an
 * unknown answer lets the action through — the endpoint behind it enforces the
 * real rule anyway, and its 409 carries the same sentence.
 */
export function useIspGate() {
   const { handleShow, handleClose } = useUIStore();
   const { data: ready } = useQuery({
      queryKey: ['isp-readiness'],
      queryFn: getIspReadiness,
      staleTime: 10_000,
   });

   const show = (
      title: string,
      message: string,
      ctaLabel: string,
      ctaHref: string
   ) =>
      handleShow({
         template: 'isp-needs-setup',
         title: '',
         windowType: 'desktop',
         data: { title, message, ctaLabel, ctaHref, onCancel: handleClose },
      });

   /**
    * Run `action`, or explain what is missing first.
    *
    * Returns nothing on purpose: the caller should not be deciding what to do
    * about a failed gate — that is the whole point of having one.
    */
   const gate = (
      need: 'server' | 'server+plan' | 'server+plan+customer',
      action: () => void
   ) => {
      if (!ready) return action();

      // Checked first for BOTH needs: a router is the deeper requirement, and
      // sending somebody to create a plan they cannot link is a wasted trip.
      if (ready.servers === 0) {
         return show(
            'Register your MikroTik first',
            'A plan is enforced by a profile on a router, and a subscriber connects through one. Register the MikroTik that serves PPPoE and this opens up.',
            'Go to Servers',
            '/isp-servers'
         );
      }
      if (need !== 'server' && ready.plans === 0) {
         return show(
            'Create an internet plan first',
            'A customer subscribes to a plan on a router — that is what an account is. Without a plan there is nothing to put them on, and no price to bill them.',
            'Go to Settings',
            '/isp-settings'
         );
      }
      /**
       * An account belongs to somebody.
       *
       * Ray, 2026-09-02: *"just prompt message instead of toast then redirect
       * to customer page. just like adding mikrotik."* Right — a toast says
       * what is wrong and then vanishes, leaving the operator on the same
       * screen with the same dead button. Every other missing prerequisite
       * here opens a door; this one had no reason to be different.
       */
      if (need === 'server+plan+customer' && ready.customers === 0) {
         return show(
            'Add a customer first',
            'An account is one plan, on one router, for one person — so there has to be a person. Add them, and SmartFi offers to set up their connection straight afterwards.',
            'Go to Customers',
            '/isp-customers'
         );
      }
      return action();
   };

   return { ready, gate };
}
